import React, { useState, useEffect } from 'react';
import { fetchCars, fetchReminders } from '../services/api';
import Cockpit from './Cockpit';
import CarForm from './CarForm';
import './Dashboard.css';

interface DashboardProps {
  userId: number;
  onAddCar: () => void;
  onAddService: (carId: number) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ userId, onAddCar, onAddService }) => {
  const [cars, setCars] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCar, setSelectedCar] = useState<any>(null);
  const [editingCar, setEditingCar] = useState<any>(null);

  const loadCars = async () => {
    try {
      const carsData = await fetchCars(userId);
      
      const carsWithReminders = await Promise.all(carsData.map(async (car: any) => {
        const reminders = await fetchReminders(car.id);
        return { ...car, reminders };
      }));
      
      setCars(carsWithReminders);
      
      // Update selected car if it exists
      if (selectedCar) {
        const updated = carsWithReminders.find(c => c.id === selectedCar.id);
        if (updated) setSelectedCar(updated);
      }
    } catch (error) {
      console.error("Error loading cars", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCarEdit = (car: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingCar(car);
  };

  useEffect(() => {
    loadCars();
  }, [userId]);

  // Si venimos de un registro exitoso y tenemos un ID de auto, lo seleccionamos automáticamente
  useEffect(() => {
    if (cars.length > 0 && !selectedCar) {
       // Buscamos si hay un auto que coincida con un posible ID persistido o pasado por props
       // Pero por simplicidad, si App.tsx maneja la vista, Dashboard solo necesita reaccionar
    }
  }, [cars]);

  if (loading) return <div className="container">Abriendo el garage...</div>;

  return (
    <div className="container">
      {editingCar && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ maxWidth: '500px', width: '100%' }}>
            <CarForm 
              userId={userId} 
              car={editingCar} 
              onSuccess={() => { setEditingCar(null); loadCars(); }} 
              onCancel={() => setEditingCar(null)} 
            />
          </div>
        </div>
      )}

      {selectedCar ? (
        <Cockpit 
          car={selectedCar} 
          onClose={() => setSelectedCar(null)} 
          onUpdate={loadCars}
          onAddService={onAddService}
        />
      ) : (
        <>
          <div className="ticker-container" style={{ marginBottom: '2rem', background: 'rgba(0,0,0,0.5)', borderBottom: '1px solid var(--accent-color)' }}>
             <div className={`ticker-text ${cars.some(car => car.reminders && car.reminders.some((r:any) => r.status === 'overdue')) ? 'warning' : ''}`}>
                {(() => {
                  const criticalCars = cars.filter(car => car.reminders && car.reminders.some((r:any) => r.status === 'overdue'));
                  const msg = criticalCars.length > 0 
                    ? `SISTEMA DE ALERTA: SE DETECTARON ${criticalCars.length} VEHÍCULO(S) CON MANTENIMIENTOS VENCIDOS --- POR FAVOR REVISAR EL GARAGE --- `
                    : 'SISTEMA DE DIAGNÓSTICO: TODOS LOS VEHÍCULOS OPERANDO BAJO PARÁMETROS NORMALES --- BIENVENIDO AL GARAGE --- ';
                  return msg + msg + msg; // Repeat text to fill the line
                })()}
             </div>
          </div>

          <header className="dashboard-header" style={{ marginBottom: '3rem' }}>
            <div>
              <h1 style={{ fontSize: '3rem', fontWeight: '900', margin: 0 }}>GARAGE</h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Diagnóstico de Flota Activo</p>
            </div>
            <button className="btn btn-primary" style={{ padding: '1rem 2rem', fontSize: '1rem' }} onClick={onAddCar}>+ REGISTRAR VEHÍCULO</button>
          </header>

          <div className="cars-grid">
            {cars.length === 0 ? (
              <div className="card empty-state" style={{ background: 'rgba(255,255,255,0.02)', border: '2px dashed rgba(255,255,255,0.1)' }}>
                <p>Tu garage está vacío.</p>
                <button className="btn btn-primary" onClick={onAddCar} style={{ marginTop: '1rem' }}>Comenzar registro</button>
              </div>
            ) : (
              cars.map(car => (
                <div key={car.id} className="card car-card-garage" onClick={() => setSelectedCar(car)} style={{ 
                  cursor: 'pointer',
                  padding: '0',
                  overflow: 'hidden',
                  borderRadius: '24px',
                  border: '1px solid rgba(255,255,255,0.05)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative'
                }}>
                  {/* Edit button floating */}
                  <button 
                    onClick={(e) => handleCarEdit(car, e)}
                    style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 10, background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >✏️</button>

                  <div style={{ height: '240px', position: 'relative' }}>
                    {car.photo_url ? (
                      <img src={car.photo_url} alt={car.make} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#111', fontSize: '5rem' }}>🚗</div>
                    )}
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '2rem', background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)' }}>
                      <h2 style={{ margin: 0, fontSize: '2rem' }}>{car.make} {car.model}</h2>
                      <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', alignItems: 'center' }}>
                        <span style={{ color: 'var(--accent-color)', fontWeight: 'bold' }}>{car.year}</span>
                        <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)' }}></span>
                        <span style={{ color: '#94a3b8' }}>{parseInt(car.current_mileage).toLocaleString()} KM</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Status strip at the bottom */}
                  <div style={{ padding: '1rem 2rem', background: 'rgba(0,0,0,0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {car.reminders && car.reminders.filter((r:any) => r.status === 'overdue').map((r:any, i:number) => (
                        <div key={i} style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ff3b30', boxShadow: '0 0 10px #ff3b30' }}></div>
                      ))}
                      {car.reminders && car.reminders.every((r:any) => r.status !== 'overdue') && (
                        <div style={{ fontSize: '0.7rem', color: '#4cd964', fontWeight: 'bold' }}>SISTEMAS OK</div>
                      )}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--accent-color)', fontWeight: 'bold' }}>ENTRAR A CABINA ›</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
